import React from 'react';
import { Box, Typography } from '@mui/material';
import { MoSpacing } from '@motivo/guanyin/src/components';
import { ResultOf } from ':src/graphql';
import { userDetailsByIdQuery } from '../../graphql/queries';
import ZeusOrganizationLink from '../shared/ZeusOrganizationLink';
import ZeusUserLink from '../shared/ZeusUserLink';
import { Section } from '../Section';

type User = NonNullable<ResultOf<typeof userDetailsByIdQuery>['userById']>;

interface SuperviseesSectionProps {
  user: User;
}

export default function SuperviseesSection({ user }: SuperviseesSectionProps) {
  const supervisees = user.superviseeMatches;
  const superviseeCount = supervisees.length;

  if (superviseeCount === 0) {
    return null;
  }

  // Group supervisees by organization
  const groupedByOrg = supervisees.reduce(
    (acc, match) => {
      const orgKey = match.supervisee.organization?.id?.toString() || 'no-org';
      if (!acc[orgKey]) {
        acc[orgKey] = {
          organization: match.supervisee.organization,
          supervisees: [],
        };
      }
      acc[orgKey].supervisees.push(match.supervisee);
      return acc;
    },
    {} as Record<
      string,
      {
        organization: User['superviseeMatches'][number]['supervisee']['organization'];
        supervisees: Array<User['superviseeMatches'][number]['supervisee']>;
      }
    >,
  );

  return (
    <Section
      title="Supervisees"
      count={superviseeCount > 0 ? superviseeCount : undefined}
      summaryLink={{
        label: 'View all matches in Zeus →',
        path: `/users/${user.id}/matches`,
      }}
    >
      <MoSpacing y={2}>
        {Object.entries(groupedByOrg).map(([orgKey, group]) => (
          <Box key={orgKey}>
            <Box mb={1.5}>
              {group.organization ? (
                <ZeusOrganizationLink organization={group.organization} fontWeight={600} />
              ) : (
                <Typography fontWeight={600}>No employer</Typography>
              )}
            </Box>
            <MoSpacing y={1}>
              {group.supervisees.map((supervisee) => (
                <Box key={supervisee.id}>
                  <div>
                    <ZeusUserLink user={supervisee} />
                  </div>
                  {supervisee.organizationUser?.organizationProgram && (
                    <Typography variant="body2" color="textSecondary">
                      {supervisee.organizationUser.organizationProgram.name}
                    </Typography>
                  )}
                </Box>
              ))}
            </MoSpacing>
          </Box>
        ))}
      </MoSpacing>
    </Section>
  );
}
